import { cardDisplayName, cardPrintedNumber } from '../utils/cardMetadata';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import MarketOverview from '../components/MarketOverview';
import PriceChart from '../components/PriceChart';
import TransactionList from '../components/TransactionList';
import { getCardIdentity, getMarketSummary, searchSoldCards } from '../api/cards';
import { platformMeta } from '../utils/market';
import type { CardInfo, MarketSummary, Transaction } from '../types';

export default function CardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const routeCardInfo = location.state?.card as CardInfo | undefined;
  const idLabel = id || routeCardInfo?.uid;

  const [resolvedCardInfo, setResolvedCardInfo] = useState<CardInfo | undefined>(routeCardInfo);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [marketSummaries, setMarketSummaries] = useState<MarketSummary[]>([]);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState<string | undefined>();
  const [identityRetryVersion, setIdentityRetryVersion] = useState(0);
  const [summaryRetryVersion, setSummaryRetryVersion] = useState(0);
  const [evidenceRetryVersion, setEvidenceRetryVersion] = useState(0);
  const activeIdRef = useRef(idLabel);

  useEffect(() => {
    if (!idLabel) return;
    let active = true;
    setResolvedCardInfo(routeCardInfo);
    setIdentityError(null);

    getCardIdentity(idLabel)
      .then((response) => {
        if (!active) return;
        if (response.code === 200 && response.data) {
          setResolvedCardInfo(response.data);
        } else {
          setIdentityError(response.message || 'Canonical identity could not be loaded.');
        }
      })
      .catch((requestError) => {
        if (active) setIdentityError(requestError?.response?.data?.message || 'Canonical identity could not be loaded.');
      });

    return () => {
      active = false;
    };
  }, [idLabel, identityRetryVersion, routeCardInfo]);

  useEffect(() => {
    if (!idLabel) return;
    let active = true;
    setSummaryError(null);

    getMarketSummary(idLabel)
      .then((response) => {
        if (!active) return;
        if (response.code === 200) {
          setMarketSummaries(response.data || []);
        } else {
          setSummaryError(response.message || 'Market summaries could not be loaded.');
        }
      })
      .catch((requestError) => {
        if (active) setSummaryError(requestError?.response?.data?.message || 'Market summaries could not be loaded.');
      });

    return () => {
      active = false;
    };
  }, [idLabel, summaryRetryVersion]);

  useEffect(() => {
    if (!idLabel) {
      setError('This card is missing a canonical resolver ID.');
      setIsLoading(false);
      return;
    }

    if (activeIdRef.current !== idLabel) {
      activeIdRef.current = idLabel;
      setTransactions([]);
      setSelectedPlatform(undefined);
      setTotalCount(0);
      setCurrentPage(1);
      setEvidenceRetryVersion((version) => version + 1);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);

    searchSoldCards(idLabel, currentPage, 20, selectedPlatform)
      .then((response) => {
        if (!active) return;
        if (response.code !== 200) {
          setError(response.message || 'Sold evidence could not be loaded.');
          return;
        }
        setTransactions((current) => currentPage === 1 ? response.data : [...current, ...response.data]);
        setTotalPages(response.totalPages);
        setTotalCount(response.total);
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || 'Sold evidence could not be loaded.');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [currentPage, evidenceRetryVersion, idLabel, selectedPlatform]);

  const evidenceCard = transactions.find((transaction) => transaction.originalCardInfo)?.originalCardInfo;
  const displayName = cardDisplayName(resolvedCardInfo) || evidenceCard?.cardName || 'Pokémon card identity';
  const displayNumber = cardPrintedNumber(resolvedCardInfo);
  const displayLanguage = resolvedCardInfo?.language || evidenceCard?.cardLanguage;
  const displayType = resolvedCardInfo?.type || evidenceCard?.cardType;
  const displayImageUrl = resolvedCardInfo?.image_url_at_self_platform || resolvedCardInfo?.image_url || evidenceCard?.cardImage;
  const hasResolvedIdentity = Boolean(resolvedCardInfo || evidenceCard);
  const selectedMarketName = selectedPlatform ? platformMeta(selectedPlatform).name : undefined;

  const retryEvidence = () => setEvidenceRetryVersion((version) => version + 1);
  const retryEvidenceUsd = () => {
    setTransactions([]);
    setTotalCount(0);
    if (currentPage === 1) {
      setEvidenceRetryVersion((version) => version + 1);
    } else {
      setCurrentPage(1);
    }
  };

  const selectPlatform = (platform?: string) => {
    setTransactions([]);
    setCurrentPage(1);
    setTotalCount(0);
    setSelectedPlatform(platform);
  };

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold text-secondary transition-colors hover:border-cyan/40 hover:text-primary">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m15 19-7-7 7-7" /></svg>
        Back to candidates
      </button>

      <section className="relative overflow-hidden rounded-[28px] bg-ink p-5 text-white shadow-[0_26px_75px_rgba(8,13,24,.20)] sm:p-7">
        <div className="pointer-events-none absolute right-[-7rem] top-[-8rem] h-[24rem] w-[24rem] rounded-full bg-cyan/10 blur-[90px]" />
        <div className="relative grid gap-6 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-center lg:grid-cols-[220px_minmax(0,1fr)]">
          <div className="mx-auto w-36 sm:w-full">
            <div className="aspect-[63/88] overflow-hidden rounded-2xl border border-white/15 bg-white/5 p-1.5 shadow-2xl">
              {displayImageUrl ? (
                <img src={displayImageUrl} alt={displayName} className="h-full w-full rounded-xl object-contain" />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-white/25">
                  <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.6-4.6a2 2 0 0 1 2.8 0L16 16m-2-2 1.6-1.6a2 2 0 0 1 2.8 0L20 14M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z" /></svg>
                  <span className="text-[9px] font-bold uppercase tracking-[0.12em]">Card image unavailable</span>
                </div>
              )}
            </div>
          </div>

          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan/20 bg-cyan/10 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-cyan">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan" /> {hasResolvedIdentity ? 'Canonical identity resolved' : 'Resolving canonical identity'}
            </div>
            <h1 className="mt-5 text-3xl font-bold leading-tight tracking-[-0.045em] sm:text-4xl lg:text-5xl">{displayName}</h1>
            {resolvedCardInfo?.series_name && <p className="mt-2 text-sm text-white/70">{resolvedCardInfo.series_name}</p>}
            {resolvedCardInfo?.metadata_status?.name_zh === 'generated' && navigator.language.startsWith('zh') && <p className="mt-2 text-sm text-white/70">译名待核实</p>}
            {resolvedCardInfo?.name && <details className="mt-2 text-xs text-white/60"><summary>Original title</summary>{resolvedCardInfo.name}</details>}
            <div className="mt-5 flex flex-wrap gap-2">
              {displayNumber && <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-xs text-white/70">{displayNumber}</span>}
              {displayLanguage && <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/60">{displayLanguage}</span>}
              {displayType && <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/60">{displayType}</span>}
            </div>
            <div className="mt-7 grid gap-3 border-t border-white/10 pt-5 sm:grid-cols-2">
              <div>
                <span className="eyebrow text-white/30">Resolver ID</span>
                <span className="mt-1 block truncate font-mono text-xs text-white/60" title={idLabel}>{idLabel || 'Unavailable'}</span>
              </div>
              <div>
                <span className="eyebrow text-white/30">Comparison model</span>
                <span className="mt-1 block text-xs text-white/60">USD normalized · sold records only</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-4 rounded-2xl border border-amber/25 bg-amber/10 px-4 py-3 text-xs leading-5 text-amber-900">
        Prices are converted to USD using the displayed FX date. Shipping, condition, and grading differences are not adjusted in this phase.
      </div>

      {identityError && !hasResolvedIdentity && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber/25 bg-amber/10 px-4 py-3" role="status">
          <p className="text-xs text-amber-900">{identityError}</p>
          <button onClick={() => setIdentityRetryVersion((version) => version + 1)} className="rounded-xl border border-amber/30 bg-white px-4 py-2 text-xs font-bold text-amber-900">Retry identity</button>
        </div>
      )}

      {error && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 px-5 py-4" role="alert">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={retryEvidence} className="rounded-xl bg-red-700 px-4 py-2 text-xs font-bold text-white">Retry sold evidence</button>
        </div>
      )}

      {summaryError && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber/25 bg-amber/10 px-4 py-3" role="status">
          <p className="text-xs text-amber-900">{summaryError}</p>
          <button onClick={() => setSummaryRetryVersion((version) => version + 1)} className="rounded-xl border border-amber/30 bg-white px-4 py-2 text-xs font-bold text-amber-900">Retry market summary</button>
        </div>
      )}

      {isLoading && currentPage === 1 ? (
        <div className="mt-8 rounded-[24px] border border-border bg-white px-6 py-20 text-center shadow-sm">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-border border-t-cyan" />
          <p className="mt-4 text-sm font-semibold text-secondary">Loading global sold evidence…</p>
        </div>
      ) : (
        <div className="mt-10 space-y-10">
          <MarketOverview
            summaries={marketSummaries}
            selectedPlatform={selectedPlatform}
            onSelectPlatform={selectPlatform}
            onRetryUsd={() => setSummaryRetryVersion((version) => version + 1)}
          />
          <PriceChart idLabel={idLabel} selectedPlatform={selectedPlatform} />
          <TransactionList
            transactions={transactions}
            totalCount={totalCount}
            loadedCount={transactions.length}
            title={selectedMarketName ? `${selectedMarketName} sold evidence` : 'All sold evidence'}
            isLoading={isLoading && currentPage > 1}
            onLoadMore={() => currentPage < totalPages && setCurrentPage((page) => page + 1)}
            hasMore={currentPage < totalPages}
            onRetryUsd={retryEvidenceUsd}
          />
        </div>
      )}
    </Layout>
  );
}
